import { Dispatch, SetStateAction, useEffect } from 'react'

import IconButton from '@components/IconButton/IconButton'
import SearchInput from '@components/SearchInput/SearchInput'
import { MarketBasketStatus } from '@domain'
import { faX } from '@fortawesome/pro-light-svg-icons'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { Stack, Tooltip } from '@mui/material'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'

import BrandOwner from './BrandOwner'
import BrandType from './BrandType'
import Category from './Category'
import Exclusion from './Exclusion'
import ExtendedPrice from './ExtendedPrice'
import Group from './Group'
import Manufacturer from './Manufacturer'
import MatchType from './MatchType'
import PackSize from './PackSize'
import Qty from './Qty'
import ReviewType from './ReviewType'
import SameManufacturer from './SameManufacturer'

type FiltersType = {
	search: string | null
	manufacturer: number | null
	category: number | null
	brandOwner: number | null
	brandTypePurchase: number | null
	brandTypeMatch: number | null
	exclusion: string | null
	group: number | null
	packSize: string | null
	matchType: string | null
	sameManufacturerSwitch: string | null
	qty: string | null
	extendedPrice: string | null
	reviewType: string | null
}

const defaultFilters = {
	search: null,
	manufacturer: null,
	category: null,
	brandOwner: null,
	brandTypePurchase: null,
	brandTypeMatch: null,
	exclusion: null,
	group: null,
	packSize: null,
	matchType: null,
	sameManufacturerSwitch: null,
	qty: null,
	extendedPrice: null,
	reviewType: null,
}

type Props = {
	filters: FiltersType
	updateFilters: Dispatch<SetStateAction<FiltersType>>
}

export default function FilterBar({ filters, updateFilters }: Props) {
	const { marketBasket } = useMarketBasketContext()
	const brandTypeFilterEnabled = useBoolVariation('feature-art-1534-brandtype-filter-update', false)

	useEffect(() => {
		if (brandTypeFilterEnabled) {
			updateFilters({ brandOwner: null })
		} else {
			updateFilters({ brandTypePurchase: null, brandTypeMatch: null })
		}
	}, [brandTypeFilterEnabled, updateFilters])

	useEffect(() => {
		if (marketBasket?.status === MarketBasketStatus.RECALCULATING) {
			// Reset filters that use data from purchases when the market basket is recalculating
			updateFilters((prevFilters) => ({
				...prevFilters,
				group: null,
				manufacturer: null,
				category: null,
				packSize: null,
			}))
		}
	}, [marketBasket, updateFilters])

	return (
		<Stack height="2.5rem" direction="row">
			<SearchInput
				value={filters.search ?? ''}
				placeholder="Description"
				size="small"
				searchOnEnter
				onChange={(e) => updateFilters({ ...filters, search: e })}
			/>
			{/* FIXME: Need to know how review types will work (filter by flagged item) */}
			{/* <Autocomplete
				sx={{ width: '10rem' }}
				size="small"
				options={['1', '2']}
				value={filters.status}
				onChange={(e, value) => updateFilters({ ...filters, status: value })}
				renderInput={(params) => <TextField {...params} placeholder="Review Type" />}
			/> */}

			<Group
				value={filters.group}
				updateFilters={updateFilters}
				label={marketBasket?.settings?.groupBy || 'Group'}
			/>
			<ReviewType value={filters.reviewType} updateFilters={updateFilters} />
			<Manufacturer value={filters.manufacturer} updateFilters={updateFilters} />
			<Category value={filters.category} updateFilters={updateFilters} />
			{brandTypeFilterEnabled ? (
				<BrandType
					brandTypePurchase={filters.brandTypePurchase}
					brandTypeMatch={filters.brandTypeMatch}
					updateFilters={updateFilters}
				/>
			) : (
				<BrandOwner value={filters.brandOwner} updateFilters={updateFilters} />
			)}
			<MatchType value={filters.matchType} updateFilters={updateFilters} />
			<Exclusion value={filters.exclusion} updateFilters={updateFilters} />
			<Qty value={filters.qty} updateFilters={updateFilters} />
			<PackSize value={filters.packSize} updateFilters={updateFilters} />
			<ExtendedPrice value={filters.extendedPrice} updateFilters={updateFilters} />
			<SameManufacturer value={filters.sameManufacturerSwitch} updateFilters={updateFilters} />

			<Tooltip title="Clear filters">
				<span>
					<IconButton
						disabled={Object.values(filters).every((value) => value === null || value === '')}
						size="small"
						onClick={() => updateFilters(defaultFilters)}
						icon={faX}
						color="error"
						sx={{ mx: 1 }}
					/>
				</span>
			</Tooltip>
		</Stack>
	)
}
